import { isAfter, subDays } from 'date-fns'
import { Capacitor } from '@capacitor/core'

import Api from 'services/Api'
import { store } from 'store/reducers'
import { setLoggedOut, renewToken } from 'store/actions/auth'
import { createNotification, createErrorNotification } from 'store/actions/notifications'
import { t } from 'translations'

// Function to load the settings
export function fetchSettings() {
  return async (dispatch, getStore) => {
    // Tell PrivateRoute that we are fetching the settings
    dispatch({ type: 'SET_IS_LOADING_SETTINGS' })

    // Fetch the settings from the backend
    const { success, ...settings } = await Api.get(`/setting`)

    if (!success) {
      dispatch(createErrorNotification())
      return setLoggedOut({ dispatch, getStore })
    }

    // Set the settings in the store
    dispatch({ type: 'SET_SETTINGS', settings })
  }
}

/**
 * Function to update the value of a setting
 * @param {string} name setting name
 * @param {any} value new setting value
 */
export function updateSetting(name, value, toast, appSetting) {
  return async dispatch => {
    // Update the value in the store
    dispatch({ type: 'SET_SETTING_VALUE', name, value })

    // Update the setting
    const { success, message } = await Api.put(`/setting`, { name, value, appSetting })

    // Renew the token to have the new lang
    if (name === 'NG_TRANSLATE_LANG_KEY') renewToken(store)

    // Show toast notification
    if (toast) {
      const notification = {
        description: '',
        title: t(`components.notification.${message}`),
        type: success ? 'success' : 'danger',
        delay: 2500,
        noDate: true
      }
      dispatch(createNotification(notification))
    }
  }
}

/**
 * Function to update the value of a setting that is only stored in redux, not persisted
 * @param {string} name setting name
 * @param {any} value new setting value
 */
export function updateLocalSetting(name, value) {
  return async dispatch => {
    // Update the value in the store
    dispatch({ type: 'SET_SETTING_VALUE', name, value })
  }
}

/**
 * Function to update the value of multiple settings
 * @param {object} values values key:value of settings to update
 * @param {boolean} appSetting are an app setting
 */
export function updateSettings(values, appSetting) {
  return async dispatch => {
    let overAllMessage = null
    // Update the setting
    const successes = await Promise.all(
      Object.keys(values).map(async name => {
        // Update the value in the store
        dispatch({ type: 'SET_SETTING_VALUE', name, value: values[name] })
        const { success, message } = await Api.put(`/setting`, { name, value: values[name], appSetting })
        if (!overAllMessage || !success) overAllMessage = message

        return success
      })
    )

    // Show toast notification
    const notification = {
      description: '',
      title: t(`components.notification.${overAllMessage}`),
      type: successes.includes(false) ? 'danger' : 'success',
      delay: 2500,
      noDate: true
    }
    dispatch(createNotification(notification))
    return !successes.includes(false)
  }
}

// Settings should be refetched after a while
export const checkIfSettingsRefreshIsNeeded = () => {
  return async (dispatch, getStore) => {
    // If we are in the browser, skip
    if (!Capacitor.isNativePlatform()) return

    // Get the lastRefreshedAt from the store
    const {
      settings: { lastRefreshedAt }
    } = getStore()
    // Resources have not been fetched yet, skip
    if (!lastRefreshedAt) return

    const nowLessOneDay = subDays(new Date(), 1)
    if (isAfter(nowLessOneDay, lastRefreshedAt)) dispatch(fetchSettings())
  }
}
